// src/pages/sales/PICreateModal.jsx
import { useState, useEffect } from "react";

const INCOTERMS = [
  { value: "EXW", label: "EXW — Ex-Warehouse" },
  { value: "DAP", label: "DAP — Delivered at Place" },
  { value: "DDP", label: "DDP — Delivered Duty Paid" },
  { value: "FOB", label: "FOB — Free on Board" },
  { value: "CFR", label: "CFR — Cost and Freight" },
  { value: "CIF", label: "CIF — Cost, Insurance & Freight" },
];

const EMPTY_LINE = { productName: "", productCode: "", quantity: "", uom: "kg", unitPrice: "", packingSize: "", isOther: false };

export function PICreateModal({ fgs, hmGrades, userName, quotations, bankAccounts, onSave, onCancel, initialPi }) {
  const today = new Date().toISOString().slice(0, 10);
  const isEdit = !!initialPi;

  function deriveCompanyType(pi) {
    return pi.piNumber?.startsWith("AMIP") ? "WB" : "HM";
  }

  const [companyType,    setCompanyType]    = useState(isEdit ? deriveCompanyType(initialPi) : "WB");
  const [currency,       setCurrency]       = useState(isEdit ? (initialPi.currency || "AED") : "AED");
  const [quotationId,    setQuotationId]    = useState(isEdit ? (initialPi.quotationId ? String(initialPi.quotationId) : "") : "");
  const [bankAccountId,  setBankAccountId]  = useState(isEdit ? (initialPi.bankAccountId ? String(initialPi.bankAccountId) : "") : "");
  const [form, setForm] = useState(isEdit ? {
    customerName:  initialPi.customerName  || "",
    shipToName:    initialPi.shipToName    || "",
    addressedTo:   initialPi.addressedTo   || "",
    contactPerson: initialPi.contactPerson || "",
    reference:     initialPi.reference     || "",
    preparedBy:    initialPi.preparedBy    || userName || "",
    date:          initialPi.date          || today,
    vatEnabled:    initialPi.vatEnabled    ?? false,
    paymentTerms:  initialPi.paymentTerms  || "",
    incoterm:      initialPi.incoterm      || "",
    notes:         initialPi.notes         || "",
  } : {
    customerName: "", shipToName: "", addressedTo: "", contactPerson: "",
    reference: "", preparedBy: userName || "", date: today,
    vatEnabled: false, paymentTerms: "", incoterm: "", notes: "",
  });
  const [lines,  setLines]  = useState(isEdit && initialPi.lines?.length
    ? initialPi.lines.map(l => ({
        productName: l.productName || "",
        productCode: l.productCode || "",
        quantity:    String(l.quantity ?? ""),
        uom:         l.uom || "kg",
        unitPrice:   String(l.unitPrice ?? ""),
        packingSize: l.packingSize || "",
        isOther:     !l.productCode,
      }))
    : [{ ...EMPTY_LINE }]);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  const businessUnit = companyType === "WB" ? "AMI" : "AMT";

  const products = companyType === "WB"
    ? (fgs || []).map(f => ({ id: String(f.id), label: f.name ? `${f.id} — ${f.name}` : String(f.id), name: f.name || String(f.id) }))
    : (hmGrades || []).filter(g => g.active).map(g => ({ id: g.id, label: g.displayName || g.id, name: g.displayName || g.id }));

  // Show all active accounts for the selected company regardless of currency
  const eligibleBankAccounts = (bankAccounts || []).filter(b =>
    b.active && b.businessUnit === businessUnit
  );

  // When "from quotation" changes, pre-fill fields
  function handleQuotationChange(qid) {
    setQuotationId(qid);
    if (!qid) return;
    const q = (quotations || []).find(q => String(q.id) === qid);
    if (!q) return;
    const isAmi = q.quoNumber?.startsWith("AMIQ");
    setCompanyType(isAmi ? "WB" : "HM");
    setCurrency(q.currency || "AED");
    setForm(f => ({
      ...f,
      customerName:  q.customerName  || "",
      shipToName:    q.shipToName    || "",
      addressedTo:   q.addressedTo   || "",
      contactPerson: q.contactPerson || "",
      reference:     q.reference     || "",
      vatEnabled:    q.vatEnabled    ?? false,
      paymentTerms:  q.paymentTerms  || "",
      incoterm:      q.incoterm      || "",
      notes:         q.notes         || "",
    }));
    if (q.lines?.length) {
      setLines(q.lines.map(l => ({
        productName: l.productName || "",
        productCode: l.productCode || "",
        quantity:    String(l.quantity ?? ""),
        uom:         l.uom || "kg",
        unitPrice:   String(l.unitPrice ?? ""),
        packingSize: l.packingSize || "",
        isOther:     !l.productCode,
      })));
    }
    setBankAccountId(""); // reset bank when quotation changes
  }

  function setF(key, val) { setForm(f => ({ ...f, [key]: val })); }

  function setLine(i, key, val) {
    setLines(ls => ls.map((l, idx) => idx === i ? { ...l, [key]: val } : l));
  }

  function selectProduct(i, value) {
    if (value === "__OTHER__") {
      setLines(ls => ls.map((l, idx) => idx === i
        ? { ...EMPTY_LINE, quantity: l.quantity, uom: l.uom, unitPrice: l.unitPrice, isOther: true }
        : l));
      return;
    }
    const p = products.find(p => p.id === value);
    if (!p) return;
    setLines(ls => ls.map((l, idx) => idx === i
      ? { ...l, productCode: p.id, productName: p.name, isOther: false }
      : l));
  }

  function backToDropdown(i) {
    setLines(ls => ls.map((l, idx) => idx === i
      ? { ...l, productCode: "", productName: "", isOther: false }
      : l));
  }

  function addLine()     { setLines(ls => [...ls, { ...EMPTY_LINE }]); }
  function removeLine(i) { setLines(ls => ls.filter((_, idx) => idx !== i)); }

  function subtotal(line) {
    const q = parseFloat(line.quantity), p = parseFloat(line.unitPrice);
    return (!isNaN(q) && !isNaN(p)) ? (q * p).toFixed(2) : "—";
  }

  function grandSubtotal() {
    return lines.reduce((sum, l) => {
      const q = parseFloat(l.quantity), p = parseFloat(l.unitPrice);
      return sum + (!isNaN(q) && !isNaN(p) ? q * p : 0);
    }, 0);
  }

  function vatAmount()  { return form.vatEnabled ? grandSubtotal() * 0.05 : 0; }
  function grandTotal() { return grandSubtotal() + vatAmount(); }

  function fmt(n) {
    return n.toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  const valid =
    form.customerName.trim() &&
    form.preparedBy.trim() &&
    lines.length > 0 &&
    lines.every(l => l.productName.trim() && l.quantity && l.unitPrice);

  async function handleSave() {
    if (!valid) return;
    setSaving(true); setError(null);
    try {
      await onSave({
        businessUnit,
        currency,
        accountId:     null,
        quotationId:   quotationId ? Number(quotationId) : null,
        bankAccountId: bankAccountId ? Number(bankAccountId) : null,
        date:          form.date,
        customerName:  form.customerName.trim(),
        shipToName:    form.shipToName.trim()    || null,
        addressedTo:   form.addressedTo.trim()   || null,
        contactPerson: form.contactPerson.trim() || null,
        reference:     form.reference.trim()     || null,
        preparedBy:    form.preparedBy.trim(),
        vatEnabled:    form.vatEnabled,
        paymentTerms:  form.paymentTerms.trim()  || null,
        incoterm:      form.incoterm             || null,
        notes:         form.notes.trim()         || null,
        lines: lines.map(l => ({
          productName: l.productName.trim(),
          productCode: l.productCode || null,
          quantity:    parseFloat(l.quantity),
          uom:         l.uom.trim(),
          unitPrice:   parseFloat(l.unitPrice),
          packingSize: l.packingSize?.trim() || null,
        })),
      });
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  }

  return (
    <div className="modal" style={{ width: 800, maxHeight: "90vh", overflowY: "auto" }}>
      <div className="modal-header">
        <div className="modal-title">{isEdit ? `Edit PI — ${initialPi.piNumber}` : "New Proforma Invoice"}</div>
        <button className="modal-close" onClick={onCancel}>✕</button>
      </div>
      <div className="modal-body">

        {/* From Quotation (optional) */}
        {(quotations || []).length > 0 && (
          <div className="form-row" style={{ marginBottom: 14 }}>
            <span className="form-label">From Quotation</span>
            <select className="form-select" style={{ flex: 1 }}
              value={quotationId}
              onChange={e => handleQuotationChange(e.target.value)}>
              <option value="">— Create without linking quotation —</option>
              {(quotations || []).map(q => (
                <option key={q.id} value={String(q.id)}>
                  {q.quoNumber} — {q.customerName}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Company toggle */}
        <div className="form-row" style={{ marginBottom: 14 }}>
          <span className="form-label">Company</span>
          {isEdit ? (
            <span style={{ padding: "4px 10px", background: "#dbeafe", color: "#1d4ed8",
              borderRadius: 4, fontSize: 12, fontWeight: 600 }}>
              {companyType === "WB" ? "AMI — Industries" : "AMT — Trading"}
            </span>
          ) : (
            <div style={{ display: "flex", gap: 8 }}>
              {[["WB","Industries (AMI)"],["HM","Trading (AMT)"]].map(([v, label]) => (
                <button key={v}
                  className={companyType === v ? "btn btn-primary" : "btn"}
                  style={{ fontSize: 12 }}
                  onClick={() => { setCompanyType(v); setLines([{ ...EMPTY_LINE }]); setBankAccountId(""); }}>
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Currency — locked if from quotation or editing */}
        <div className="form-row" style={{ marginBottom: 14 }}>
          <span className="form-label">Currency</span>
          {(quotationId || isEdit) ? (
            <span style={{ padding: "4px 10px", background: "#dbeafe", color: "#1d4ed8",
              borderRadius: 4, fontSize: 12, fontWeight: 600 }}>{currency}</span>
          ) : (
            <select className="form-select" style={{ width: 120 }}
              value={currency} onChange={e => { setCurrency(e.target.value); setBankAccountId(""); }}>
              <option value="AED">AED</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
            </select>
          )}
        </div>

        {/* Bank Account selector */}
        <div className="form-row" style={{ marginBottom: 14 }}>
          <span className="form-label">Bank Account</span>
          <select className="form-select" style={{ flex: 1 }}
            value={bankAccountId}
            onChange={e => setBankAccountId(e.target.value)}>
            <option value="">— No bank details on PI —</option>
            {eligibleBankAccounts.map(b => (
              <option key={b.id} value={String(b.id)}>
                {b.bankName} · {b.accountNumber} ({b.currency})
              </option>
            ))}
          </select>
        </div>

        <div className="form-sep" />

        {/* Customer info */}
        <div className="form-row">
          <span className="form-label">Customer Name *</span>
          <input className="form-input" style={{ flex: 1 }}
            value={form.customerName} onChange={e => setF("customerName", e.target.value)} />
        </div>
        <div className="form-row">
          <span className="form-label">Ship To Name</span>
          <input className="form-input" style={{ flex: 1 }}
            value={form.shipToName} onChange={e => setF("shipToName", e.target.value)} />
        </div>
        <div className="form-row">
          <span className="form-label">Kind Attn</span>
          <input className="form-input" style={{ flex: 1 }}
            value={form.addressedTo} onChange={e => setF("addressedTo", e.target.value)} />
        </div>
        <div className="form-row">
          <span className="form-label">Contact Person</span>
          <input className="form-input" style={{ flex: 1 }}
            value={form.contactPerson} onChange={e => setF("contactPerson", e.target.value)} />
        </div>
        <div className="form-row">
          <span className="form-label">Customer Ref</span>
          <input className="form-input" style={{ flex: 1 }}
            value={form.reference} onChange={e => setF("reference", e.target.value)} />
        </div>
        <div className="form-row">
          <span className="form-label">Prepared By</span>
          <input className="form-input" style={{ flex: 1, background: "#f3f4f6", color: "#374151" }}
            value={form.preparedBy} readOnly />
        </div>
        <div className="form-row">
          <span className="form-label">Date *</span>
          <input className="form-input" type="date" value={form.date}
            onChange={e => setF("date", e.target.value)} style={{ width: 160 }} />
        </div>

        <div className="form-sep" />

        {/* Line items */}
        <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 8 }}>
          Line Items <span style={{ fontWeight: 400, color: "#6b7280" }}>(at least one required)</span>
        </div>

        {lines.map((line, i) => (
          <div key={i} style={{ border: "1px solid #e5e7eb", borderRadius: 6,
            padding: "10px 12px", marginBottom: 8, background: "#fafafa" }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>

              <div style={{ flex: "2 1 220px" }}>
                <div style={{ fontSize: 11, color: "#555", marginBottom: 2 }}>Product *</div>
                {line.isOther ? (
                  <div style={{ display: "flex", gap: 4 }}>
                    <input className="form-input" style={{ flex: 1 }}
                      placeholder="Enter product name"
                      value={line.productName}
                      onChange={e => setLine(i, "productName", e.target.value)} />
                    <button className="btn-small" style={{ fontSize: 10, whiteSpace: "nowrap" }}
                      onClick={() => backToDropdown(i)}>← List</button>
                  </div>
                ) : (
                  <select className="form-select" style={{ width: "100%" }}
                    value={line.productCode}
                    onChange={e => selectProduct(i, e.target.value)}>
                    <option value="">— Select —</option>
                    {products.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                    <option value="__OTHER__">Other (type manually)</option>
                  </select>
                )}
              </div>

              <div style={{ flex: "0 1 70px" }}>
                <div style={{ fontSize: 11, color: "#555", marginBottom: 2 }}>Qty *</div>
                <input className="form-input" type="number" min="0" step="0.001"
                  style={{ width: "100%" }} value={line.quantity}
                  onChange={e => setLine(i, "quantity", e.target.value)} />
              </div>
              <div style={{ flex: "0 1 60px" }}>
                <div style={{ fontSize: 11, color: "#555", marginBottom: 2 }}>UOM</div>
                <input className="form-input" style={{ width: "100%" }}
                  value={line.uom}
                  onChange={e => setLine(i, "uom", e.target.value)} />
              </div>
              <div style={{ flex: "0 1 95px" }}>
                <div style={{ fontSize: 11, color: "#555", marginBottom: 2 }}>Price ({currency}) *</div>
                <input className="form-input" type="number" min="0" step="0.0001"
                  style={{ width: "100%", textAlign: "right" }} value={line.unitPrice}
                  onChange={e => setLine(i, "unitPrice", e.target.value)} />
              </div>
              <div style={{ flex: "0 1 100px" }}>
                <div style={{ fontSize: 11, color: "#555", marginBottom: 2 }}>Packing</div>
                <input className="form-input" style={{ width: "100%" }}
                  placeholder="e.g. 20 KG Pail"
                  value={line.packingSize}
                  onChange={e => setLine(i, "packingSize", e.target.value)} />
              </div>
              <div style={{ flex: "0 1 90px", textAlign: "right" }}>
                <div style={{ fontSize: 11, color: "#555", marginBottom: 2 }}>Subtotal</div>
                <div style={{ fontWeight: 600, fontSize: 13, padding: "6px 0" }}>{subtotal(line)}</div>
              </div>
              <div>
                {lines.length > 1 && (
                  <button className="btn-small" style={{ color: "#dc2626" }}
                    onClick={() => removeLine(i)}>✕</button>
                )}
              </div>
            </div>
          </div>
        ))}

        <button className="btn" style={{ fontSize: 12, marginBottom: 12 }}
          onClick={addLine}>+ Add Line</button>

        <div style={{ textAlign: "right", fontSize: 12, marginBottom: 12, paddingRight: 4 }}>
          <div>Sub Total: <strong>{currency} {fmt(grandSubtotal())}</strong></div>
          {form.vatEnabled && <div>VAT 5%: <strong>{currency} {fmt(vatAmount())}</strong></div>}
          <div style={{ fontSize: 14, marginTop: 2 }}>Total: <strong>{currency} {fmt(grandTotal())}</strong></div>
        </div>

        <div className="form-sep" />

        {/* Terms */}
        <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 8 }}>Terms &amp; Conditions</div>

        <div className="form-row">
          <span className="form-label">Payment Terms</span>
          <input className="form-input" style={{ flex: 1 }}
            placeholder="e.g. 30 Days PDC on Delivery"
            value={form.paymentTerms} onChange={e => setF("paymentTerms", e.target.value)} />
        </div>

        <div className="form-row">
          <span className="form-label">Delivery (Incoterm)</span>
          <select className="form-select" style={{ width: 280 }}
            value={form.incoterm} onChange={e => setF("incoterm", e.target.value)}>
            <option value="">— Select —</option>
            {INCOTERMS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>

        <div className="form-row" style={{ alignItems: "center" }}>
          <span className="form-label">VAT 5%</span>
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
            <input type="checkbox" checked={form.vatEnabled}
              onChange={e => setF("vatEnabled", e.target.checked)} />
            Include 5% VAT in total
          </label>
        </div>

        <div className="form-row" style={{ alignItems: "flex-start" }}>
          <span className="form-label" style={{ paddingTop: 6 }}>Notes</span>
          <textarea className="form-input" style={{ flex: 1, minHeight: 70, resize: "vertical" }}
            value={form.notes} onChange={e => setF("notes", e.target.value)} />
        </div>

        {error && <div className="alert-warn" style={{ marginTop: 8 }}>{error}</div>}

        <div className="form-sep" />
        <div className="form-footer">
          <button className="btn" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" disabled={!valid || saving}
            onClick={handleSave}>{saving ? "Saving…" : isEdit ? "Save Changes" : "Create PI"}</button>
        </div>
      </div>
    </div>
  );
}
